import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { partition } from '@utilities/helpers/common';
import { useTranslation } from 'next-i18next';
import HTMLText from '@features/PageBuilder/components/HTMLText';
import useDateTime from '@hooks/use-datetime';
import { useFormatPrice } from '@components/Price';
import { useSelector } from 'react-redux';
import {
  Root,
  OrderItemsList as OrderItemsListClass,
  FixedTotalsOnMobile,
  ForceStickToBottom,
  HeaderContainer,
  Header as HeaderClass,
  MutedText,
} from './OrderSummary.module.css';
import OrderItems from './components/OrderItems';

import OrderItemsSkeleton from './components/OrderItems/Skeleton';

const OrderSummary = ({
  className,
  fixedTotalsOnMobile,
  forceStickToBottom,
  animate,
  memberships,
  checkoutPrice,
  isLoading,
}) => {
  const { t } = useTranslation(['checkout']);
  const [preOrderMemberships, nonPreOrderMemberships] = partition(
    memberships,
    ({ summary: { plans = [] } }) => plans?.[0]?.is_pre_order
  );
  const {
    options: { priceModels, countryCode },
  } = useSelector((state) => state.wordpress);
  const { format } = useDateTime();
  const { isSingle } = useSelector((state) => state.checkout);
  const showsPriceChanges =
    priceModels?.find((priceWarning) => priceWarning.priceWarning)
      ?.priceWarning !== '' && !['DE'].includes(countryCode);
  const priceChangesText = priceModels?.find(
    (priceWarning) => priceWarning.priceWarning
  )?.priceWarning;
  const classes = classNames(
    Root,
    {
      [FixedTotalsOnMobile]: fixedTotalsOnMobile,
      [ForceStickToBottom]: forceStickToBottom,
    },
    className
  );
  const amount = useFormatPrice({
    amount: checkoutPrice?.totalMonthly / 100,
    showDecimals: true,
    noCurrencySymbol: true,
  });

  const preOrderDate = () => {
    if (preOrderMemberships.length > 1)
      return t('startDate').toLocaleLowerCase();
    return format(preOrderMemberships[0]?.plans[0]?.start_date, 'dd.MM.yyyy');
  };

  return (
    <div className={classes}>
      <div className={HeaderContainer}>
        <div className={HeaderClass}>{t('yourBasket', { ns: 'checkout' })}</div>
      </div>
      {isLoading ||
      (!preOrderMemberships.length > 0 &&
        !nonPreOrderMemberships.length > 0) ? (
        <div className={OrderItemsListClass}>
          <OrderItemsSkeleton />
        </div>
      ) : (
        <>
          {preOrderMemberships.length > 0 && (
            <>
              <OrderItems
                className={OrderItemsListClass}
                memberships={preOrderMemberships}
                isLoading={isLoading}
                animate={animate}
                membershipsIdentifier={memberships}
                checkoutPrice={checkoutPrice}
                isPreOrder
              />
              {!isSingle && (
                <div className={MutedText}>
                  {t('monthlyPriceAndTerminationDatePreOrder', {
                    ns: 'checkout',
                    amount,
                    date: preOrderDate(),
                  })}
                </div>
              )}
            </>
          )}
          {nonPreOrderMemberships.length > 0 && (
            <>
              <OrderItems
                className={OrderItemsListClass}
                memberships={nonPreOrderMemberships}
                isLoading={isLoading}
                animate={animate}
                membershipsIdentifier={memberships}
                checkoutPrice={checkoutPrice}
              />
              {!isSingle && (
                <div className={MutedText}>
                  {t('monthlyPriceAndTerminationDate', {
                    ns: 'checkout',
                    amount,
                    date: format(
                      checkoutPrice.firstPossibleTerminationDate,
                      'dd.MM.yyyy'
                    ),
                  })}
                </div>
              )}
              {showsPriceChanges && (
                <div className={MutedText}>
                  <HTMLText>{priceChangesText}</HTMLText>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

OrderSummary.propTypes = {
  fixedTotalsOnMobile: PropTypes.bool,
  animate: PropTypes.bool,
};
OrderSummary.defaultProps = {
  fixedTotalsOnMobile: false,
  animate: false,
};

export default OrderSummary;
